import { useState, useContext, useEffect } from 'react';
import { graphql } from 'react-relay';
import { useMutationInterop } from '@confluence/apollo-relay-interop/entry-points/useMutationInterop.ts';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { SPAViewContext } from '@confluence/spa-view-context/entry-points/SPAViewContext.ts';
import { confluenceLocalStorageInstance as localStorage } from '@confluence/storage-manager/entry-points/storageInstance.ts';
import { keys } from '@confluence/storage-manager/entry-points/LocalStorageKeys.ts';
import { fg } from '@confluence/feature-gating';

import type {
	InitAiFirstCreationExpMutation as InitAiFirstCreationExpMutationType,
	InitAiFirstCreationExpMutationVariables,
} from './__types__/InitAiFirstCreationExpMutation.ts';

export const AIFC_EXP_TRAIT_NAME = 'confluence_aifc';

/**
 * This component is used to initialize the AI First Creation experiment.
 * It does not render anything.
 */
export const InitAiFirstCreationExp = () => {
	const [isInitialized, setIsInitialized] = useState(false);

	const { cloudId, isAdminHubAIEnabled, isRovoEnabled } = useSessionData();
	const [initAiFirstCreationExpMutation] = useMutationInterop<
		InitAiFirstCreationExpMutationType,
		InitAiFirstCreationExpMutationVariables
	>(
		graphql`
			mutation InitAiFirstCreationExpMutation($cloudId: ID!) {
				confluence_experimentInitAiFirstCreation(cloudId: $cloudId) {
					success
				}
			}
		`,
		{
			variables: { cloudId },
		},
	);

	const { experimentFeatures, loading: isSPAViewContextLoading } = useContext(SPAViewContext);

	const hasInitializedAiFirstCreationExp = localStorage.getItem(keys.HAS_INIT_AIFC_EXP);

	useEffect(() => {
		if (
			isInitialized ||
			(hasInitializedAiFirstCreationExp && !fg('init_aifc_if_disabled')) ||
			isSPAViewContextLoading ||
			!experimentFeatures ||
			!isAdminHubAIEnabled ||
			!isRovoEnabled ||
			!fg('cc_aifc_exp_gate')
		) {
			return;
		}
		try {
			const aifcExpTraitValue = JSON.parse(experimentFeatures)?.[AIFC_EXP_TRAIT_NAME];

			if (
				// aifc experiment trait is not set
				aifcExpTraitValue === 'null' ||
				!aifcExpTraitValue ||
				(aifcExpTraitValue === 'exp-control' && fg('init_aifc_if_disabled'))
			) {
				setIsInitialized(true);
				void initAiFirstCreationExpMutation().then(({ data }) => {
					if (data?.confluence_experimentInitAiFirstCreation?.success) {
						localStorage.setItem(keys.HAS_INIT_AIFC_EXP, 'true');
					}
				});
			}
		} catch {
			// do nothing
		}
	}, [
		isInitialized,
		isAdminHubAIEnabled,
		isSPAViewContextLoading,
		experimentFeatures,
		initAiFirstCreationExpMutation,
		hasInitializedAiFirstCreationExp,
		isRovoEnabled,
	]);

	return null;
};
